/**
 * Maps FHIR resource types to their domain logging keys.
 *
 * Organization and Practitioner are technically ambiguous (an Organization could
 * be a Practice or an Institution; a Practitioner could be an internal Provider
 * or an external HealthCareProvider). We consider this safe because these keys
 * are only used for logging/observability — a slightly imprecise tag is still
 * far more useful than no tag at all when searching Sentry or BetterStack.
 */
export const REFERENCE_TYPE_TO_LOG_KEY: Record<string, string> = {
  Patient: "memberId",
  Organization: "practiceId",
  Practitioner: "providerId",
  Encounter: "encounterId",
  Appointment: "appointmentId",
  ServiceRequest: "orderId",
};

/**
 * Canonical domain keys used for logging and Sentry tagging.
 *
 * Derived from REFERENCE_TYPE_TO_LOG_KEY (FHIR-backed keys) plus domain keys
 * that don't map to a single FHIR resource type (e.g. faxId, responseId).
 *
 * Single source of truth — the Inngest domain-context middleware and the
 * server-action framework both use this list (via setDomainSentryTags) to
 * tag errors with `vibrant.*` Sentry tags.
 */
export const CRITICAL_LOG_KEYS = [
  ...Object.values(REFERENCE_TYPE_TO_LOG_KEY),
  "faxId",
  "surveyId",
  "responseId",
  "questionnaireSessionId",
  "taskId",
] as const;

const TRAILING_REFERENCE_REGEX = /([A-Za-z][A-Za-z0-9]*)\/([^/?#]+?)(?:\/_history\/[^/?#]+)?\/?$/;

/**
 * Builds a canonical `ResourceType/id` reference when both parts are present.
 */
export function buildResourceReference({
  resourceType,
  resourceId,
}: {
  resourceType?: string;
  resourceId?: string;
}): string | undefined {
  if (!resourceType || !resourceId) {
    return undefined;
  }

  return `${resourceType}/${resourceId}`;
}

/**
 * Normalizes relative or absolute references to a canonical `ResourceType/id` shape.
 */
export function normalizeReference(reference: string | undefined): string | undefined {
  if (typeof reference !== "string") {
    return undefined;
  }

  const trimmedReference = reference.trim();
  if (trimmedReference.length === 0) {
    return undefined;
  }

  const [referenceWithoutQueryOrFragment] = trimmedReference.split(/[?#]/);
  if (!referenceWithoutQueryOrFragment) {
    return undefined;
  }

  const match = referenceWithoutQueryOrFragment.match(TRAILING_REFERENCE_REGEX);
  if (!match) {
    return undefined;
  }

  const resourceType = match[1];
  const resourceId = match[2];
  if (!resourceType || !resourceId) {
    return undefined;
  }

  return `${resourceType}/${resourceId}`;
}

/**
 * Normalizes a resource reference when it matches the expected resource type.
 */
export function normalizeResourceReference({
  reference,
  resourceType,
}: {
  reference?: string;
  resourceType: string;
}): string | undefined {
  const normalizedReference = normalizeReference(reference);
  return normalizedReference?.startsWith(`${resourceType}/`) ? normalizedReference : undefined;
}

/**
 * Normalizes a Patient reference when it resolves to `Patient/{id}`.
 */
export function normalizePatientReference(reference: string | undefined): string | undefined {
  return normalizeResourceReference({ reference, resourceType: "Patient" });
}

/**
 * Normalizes an Organization reference when it resolves to `Organization/{id}`.
 */
export function normalizeOrganizationReference(reference: string | undefined): string | undefined {
  return normalizeResourceReference({ reference, resourceType: "Organization" });
}

/**
 * Extracts the resource ID when a reference matches the expected FHIR resource type.
 */
export function parseResourceIdFromReference({
  reference,
  resourceType,
}: {
  reference?: string;
  resourceType: string;
}): string | undefined {
  const normalizedReference = normalizeResourceReference({ reference, resourceType });
  return normalizedReference?.slice(`${resourceType}/`.length);
}

/**
 * Extracts domain logging metadata from one or more FHIR reference strings.
 *
 * Only maps unambiguous resource types (Patient → memberId, Encounter → encounterId, etc.).
 * Skips undefined/null values and references whose resource type has no clear domain key.
 *
 * @example
 * ```ts
 * // Simple usage
 * logger.info("Processing fax", {
 *   ...getLoggingMetadata(fax.subject?.reference),
 * });
 *
 * // Multiple references
 * logger.info("Processing encounter", {
 *   ...getLoggingMetadata(encounter.subject?.reference, encounter.appointment?.[0]?.reference),
 * });
 *
 * // Mix with explicit keys for ambiguous types
 * logger.info("Updating fax status", {
 *   ...getLoggingMetadata(fax.subject?.reference),
 *   practiceId: fax.sender?.reference?.split("/")[1],
 * });
 * ```
 */
export function getLoggingMetadata(
  ...references: (string | undefined | null)[]
): Record<string, string> {
  const metadata: Record<string, string> = {};

  for (const ref of references) {
    if (!ref) continue;

    const normalizedReference = normalizeReference(ref) ?? ref;
    const parts = normalizedReference.split("/");
    const resourceType = parts.at(-2);
    const id = parts.at(-1);
    if (!resourceType || !id) continue;

    const logKey = REFERENCE_TYPE_TO_LOG_KEY[resourceType];
    if (logKey) {
      metadata[logKey] = id;
    }
  }

  return metadata;
}

/**
 * Parses an Encounter ID from a FHIR reference string.
 *
 * Handles formats like "Encounter/abc-123" and "https://api.medplum.com/fhir/R4/Encounter/abc-123".
 */
export function parseEncounterIdFromReference({
  encounterRef,
}: {
  encounterRef: string;
}): string | undefined {
  const normalizedReference = normalizeResourceReference({
    reference: encounterRef,
    resourceType: "Encounter",
  });
  if (!normalizedReference) {
    return undefined;
  }

  const encounterId = normalizedReference.split("/")[1];
  if (!encounterId || encounterId === "Encounter") {
    return undefined;
  }

  if (!/^[A-Za-z0-9.-]+$/.test(encounterId)) {
    return undefined;
  }

  return encounterId;
}
